import {
  QueryConstraint,
  count,
  documentId,
  getAggregateFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { COLLECTIONS } from "./collectionNames";
import { adminCollection, logDevelopmentError } from "./firestoreHelpers";
import { mapShoutOutRecord, mapUserRecord } from "./realtimeMappers";
import { AdminUserRecord, ShoutOutRecord } from "./types";

export type ShoutOutFilters = {
  startDate: string;
  endDate: string;
  reason: string;
  recipientRole: string;
  giverRole: string;
};

export type EnrichedShoutOut = ShoutOutRecord & {
  recipientName: string;
  recipientRole: string;
  giverName: string;
  giverRole: string;
};

export type RankedUser = {
  userId: string;
  name: string;
  role: string;
  count: number;
};

export type ReasonSummary = {
  reason: string;
  count: number;
};

export type ShoutOutsAdminData = {
  totalShoutOuts: number | null;
  shoutOutsToday: number | null;
  filteredShoutOuts: EnrichedShoutOut[];
  topRecipients: RankedUser[];
  commonReasons: ReasonSummary[];
  zeroShoutOutsUnavailableReason: string;
  lastUpdated: Date;
};

const EMPTY_FILTERS: ShoutOutFilters = {
  startDate: "",
  endDate: "",
  reason: "",
  recipientRole: "",
  giverRole: ""
};

function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function startOfTomorrow(): Date {
  const today = startOfToday();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
}

function dateFromInput(value: string, endOfDay = false): Date | null {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return endOfDay ? new Date(year, month - 1, day + 1) : new Date(year, month - 1, day);
}

async function safeCount(constraints: QueryConstraint[] = []): Promise<number | null> {
  try {
    const snapshot = await getAggregateFromServer(query(adminCollection(COLLECTIONS.peerShoutOuts), ...constraints), {
      total: count()
    });
    return snapshot.data().total;
  } catch (error) {
    logDevelopmentError(error);
    return null;
  }
}

async function fetchUsers(ids: string[]): Promise<Map<string, AdminUserRecord>> {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean))).slice(0, 30);
  const users = new Map<string, AdminUserRecord>();

  for (let index = 0; index < uniqueIds.length; index += 10) {
    const chunk = uniqueIds.slice(index, index + 10);
    try {
      const snapshot = await getDocs(query(adminCollection(COLLECTIONS.users), where(documentId(), "in", chunk)));
      snapshot.docs.forEach((document) => users.set(document.id, mapUserRecord(document.id, document.data())));
    } catch (error) {
      logDevelopmentError(error);
    }
  }

  return users;
}

function roleText(user: AdminUserRecord | undefined): string {
  if (!user) {
    return "Unavailable";
  }

  if (user.roles.length > 0) {
    return user.roles.join(", ");
  }

  return user.role ?? "Unavailable";
}

function displayName(user: AdminUserRecord | undefined, fallback: string | null, userId: string | null): string {
  return user?.displayName ?? user?.username ?? fallback ?? userId ?? "Unavailable";
}

function matchesRole(filter: string, role: string): boolean {
  return !filter || role.toLowerCase().split(",").map((part) => part.trim()).includes(filter.toLowerCase());
}

function buildTopRecipients(shoutOuts: EnrichedShoutOut[]): RankedUser[] {
  const grouped = new Map<string, RankedUser>();

  for (const shoutOut of shoutOuts) {
    const userId = shoutOut.recipientDancerUserId;
    if (!userId) {
      continue;
    }

    const current = grouped.get(userId);
    grouped.set(userId, {
      userId,
      name: shoutOut.recipientName,
      role: shoutOut.recipientRole,
      count: (current?.count ?? 0) + 1
    });
  }

  return Array.from(grouped.values())
    .sort((left, right) => right.count - left.count)
    .slice(0, 10);
}

function buildCommonReasons(shoutOuts: EnrichedShoutOut[]): ReasonSummary[] {
  const grouped = new Map<string, number>();

  for (const shoutOut of shoutOuts) {
    const reason = shoutOut.topic ?? "Unavailable";
    grouped.set(reason, (grouped.get(reason) ?? 0) + 1);
  }

  return Array.from(grouped.entries())
    .map(([reason, reasonCount]) => ({ reason, count: reasonCount }))
    .sort((left, right) => right.count - left.count)
    .slice(0, 8);
}

export async function fetchShoutOutsAdminData(filters: ShoutOutFilters = EMPTY_FILTERS): Promise<ShoutOutsAdminData> {
  const queryConstraints: QueryConstraint[] = [];
  const startDate = dateFromInput(filters.startDate);
  const endDate = dateFromInput(filters.endDate, true);

  if (filters.reason) {
    queryConstraints.push(where("topic", "==", filters.reason));
  }

  if (startDate) {
    queryConstraints.push(where("createdAt", ">=", startDate));
  }

  if (endDate) {
    queryConstraints.push(where("createdAt", "<", endDate));
  }

  queryConstraints.push(orderBy("createdAt", "desc"), limit(100));

  const [totalShoutOuts, shoutOutsToday, snapshot] = await Promise.all([
    safeCount(),
    safeCount([where("createdAt", ">=", startOfToday()), where("createdAt", "<", startOfTomorrow())]),
    getDocs(query(adminCollection(COLLECTIONS.peerShoutOuts), ...queryConstraints))
  ]);

  const shoutOuts = snapshot.docs.map((document) => mapShoutOutRecord(document.id, document.data()));
  const users = await fetchUsers(
    shoutOuts.flatMap((shoutOut) => [shoutOut.recipientDancerUserId, shoutOut.senderUserId].filter(Boolean) as string[])
  );

  const enriched = shoutOuts
    .map((shoutOut): EnrichedShoutOut => {
      const recipient = shoutOut.recipientDancerUserId ? users.get(shoutOut.recipientDancerUserId) : undefined;
      const giver = shoutOut.senderUserId ? users.get(shoutOut.senderUserId) : undefined;

      return {
        ...shoutOut,
        recipientName: displayName(recipient, null, shoutOut.recipientDancerUserId),
        recipientRole: roleText(recipient),
        giverName: displayName(giver, shoutOut.senderDisplayNameSnapshot, shoutOut.senderUserId),
        giverRole: roleText(giver)
      };
    })
    .filter((shoutOut) => matchesRole(filters.recipientRole, shoutOut.recipientRole))
    .filter((shoutOut) => matchesRole(filters.giverRole, shoutOut.giverRole));

  return {
    totalShoutOuts,
    shoutOutsToday,
    filteredShoutOuts: enriched,
    topRecipients: buildTopRecipients(enriched),
    commonReasons: buildCommonReasons(enriched),
    zeroShoutOutsUnavailableReason:
      "Unavailable: calculating users with zero shout-outs would require comparing the full users collection against shout-out recipients. TODO: add a server-owned aggregate such as adminAnalytics/usersWithZeroShoutOuts.",
    lastUpdated: new Date()
  };
}

export const SHOUT_OUT_REASONS = ["inspired_me", "was_kind", "helped_me_grow", "great_teammate", "encouraged_me"];
